/*
 * buttr — acronym guard
 * ------------------------------------------------------------------
 * The brand renders in lower case sitewide (see buttr-brand.css), but the
 * client's direction is that acronyms keep their capitals: "AED", "UAE".
 *
 * CSS alone cannot do this — text-transform applies to a whole element, and
 * Shopify's rich-text settings reject class attributes, so acronyms inside
 * admin-entered copy cannot be hand-wrapped. This walks text nodes and wraps
 * any listed acronym in <span class="buttr-caps">, which buttr-brand.css
 * exempts from the lowercase rule.
 *
 * Because it runs against rendered text, it also covers copy the client adds
 * in Shopify admin later — no theme edit required.
 */
(function () {
  'use strict';

  var ACRONYMS = ['AED', 'UAE', 'VAT'];

  /*
   * Unit symbols are not acronyms, but they carry the same rule: on a food
   * product "1L" is a litre and "1l" is a typo. Eight of the seventeen pantry
   * titles render "1l" today, because the sitewide lowercase transform eats the
   * symbol and \b(L)\b never fires — there is no word boundary between a digit
   * and the letter that follows it.
   *
   * Matched in the SAME pass as the acronyms rather than a second walk: wrap()
   * replaces the text node it works on, so a second pass over the same node
   * would find nothing. Group 1 is an acronym; groups 2+3 are a number and its
   * unit, and only the unit is capitalised.
   *
   * Written without lookbehind on purpose — Safari below 16.4 throws on the
   * regex literal, which would take AED and UAE down with it.
   */
  var PATTERN = new RegExp(
    '\\b(' + ACRONYMS.join('|') + ')\\b' + '|' + '(\\d+(?:\\.\\d+)?\\s?)(L|ML|KG)\\b',
    'g'
  );
  var PATTERN_I = new RegExp('\\b(' + ACRONYMS.join('|') + ')\\b', 'gi');

  // Elements whose text must never be rewritten: script/style payloads, and
  // anything the customer is typing into.
  var SKIP = /^(SCRIPT|STYLE|NOSCRIPT|TEXTAREA|INPUT|SELECT|OPTION|SVG|TEMPLATE)$/;

  function shouldSkip(node) {
    for (var el = node.parentElement; el; el = el.parentElement) {
      if (SKIP.test(el.tagName)) return true;
      if (el.classList && el.classList.contains('buttr-caps')) return true;
      if (el.isContentEditable) return true;
    }
    return false;
  }

  function wrap(textNode) {
    var text = textNode.nodeValue;
    PATTERN.lastIndex = 0;
    if (!PATTERN.test(text)) return;

    var frag = document.createDocumentFragment();
    var cursor = 0;
    PATTERN.lastIndex = 0;

    var match;
    while ((match = PATTERN.exec(text)) !== null) {
      if (match.index > cursor) {
        frag.appendChild(document.createTextNode(text.slice(cursor, match.index)));
      }
      // match[1] = acronym. Otherwise match[2] is the number and match[3] the
      // unit symbol, and only the symbol is exempted from the lowercase rule.
      var caps = match[1] || match[3];
      var prefix = match[1] ? '' : match[2];
      if (prefix) frag.appendChild(document.createTextNode(prefix));

      var span = document.createElement('span');
      span.className = 'buttr-caps';
      span.textContent = caps;
      frag.appendChild(span);
      cursor = match.index + match[0].length;
    }
    if (cursor < text.length) {
      frag.appendChild(document.createTextNode(text.slice(cursor)));
    }
    textNode.parentNode.replaceChild(frag, textNode);
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || node.nodeValue.indexOf(' ') === -1 && node.nodeValue.length > 64) {
          return NodeFilter.FILTER_REJECT;
        }
        return shouldSkip(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      },
    });

    // Collect first — wrap() mutates the tree, which would invalidate the walker.
    var nodes = [];
    var current;
    while ((current = walker.nextNode())) nodes.push(current);
    nodes.forEach(wrap);
  }

  // Placeholders are UI copy ("Search", "Email address"), but they live inside
  // inputs — which buttr-brand.css exempts from the lowercase rule so the
  // customer's own typing is never rewritten. text-transform is not reliably
  // honoured on ::placeholder, so lower the attribute directly and put the
  // acronyms back.
  function lowerPlaceholders(root) {
    if (!root || root.nodeType !== 1) return;
    var fields = root.querySelectorAll('input[placeholder], textarea[placeholder]');
    if (root.matches && root.matches('input[placeholder], textarea[placeholder]')) {
      fields = [root].concat(Array.prototype.slice.call(fields));
    }
    Array.prototype.forEach.call(fields, function (field) {
      var current = field.getAttribute('placeholder');
      if (!current || field.dataset.buttrPlaceholder === current) return;
      var lowered = current.toLowerCase().replace(PATTERN_I, function (m) {
        return m.toUpperCase();
      });
      field.setAttribute('placeholder', lowered);
      field.dataset.buttrPlaceholder = lowered;
    });
  }

  function init() {
    scan(document.body);
    lowerPlaceholders(document.body);

    // Cart drawers, quick-add and section re-renders inject new markup after
    // load. Re-scan only the subtrees that actually changed; nodes we created
    // are skipped by shouldSkip(), so this cannot feed itself.
    if (!('MutationObserver' in window)) return;
    var queue = [];
    var scheduled = false;

    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        for (var i = 0; i < m.addedNodes.length; i++) {
          if (m.addedNodes[i].nodeType === 1) queue.push(m.addedNodes[i]);
        }
      });
      if (queue.length && !scheduled) {
        scheduled = true;
        requestAnimationFrame(function () {
          scheduled = false;
          var batch = queue;
          queue = [];
          batch.forEach(function (node) {
            scan(node);
            lowerPlaceholders(node);
          });
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
